/**
 * SpoilagePage — crop-loss tracking (§55.7). Log fruit lost as: not picked (left
 * on the tree), picked-but-not-sold, or graded out — by variety/block, with a
 * quantity + estimated lost value. A loss report rolls it up by type/variety/reason.
 */
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2, Plus, X } from 'lucide-react'
import { spoilageApi, varietiesApi, blocksApi, prefsApi, pricingApi, type SpoilageEvent, type SpoilageType, type UserPreferences } from '../lib/api'
import { weightUnit, inputToKg, kgToDisplayUnit, type MeasurementSystem } from '../lib/units'

const TYPES: { v: SpoilageType; label: string; hint: string; color: string }[] = [
  { v: 'not_picked', label: 'Not picked', hint: 'Left on the tree past prime', color: '#d97706' },
  { v: 'not_sold', label: 'Not sold', hint: 'Picked but packed out / dumped', color: '#dc2626' },
  { v: 'grade_out', label: 'Grade-out', hint: 'Too small / damaged', color: '#9333ea' },
]
const REASONS = ['weather', 'labor', 'market', 'pest', 'disease', 'oversupply', 'other']
const typeMeta = (t: string) => TYPES.find(x => x.v === t) || TYPES[0]

const card: React.CSSProperties = { background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16 }
const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)' }

export function SpoilagePage() {
  const qc = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const { data: prefsData } = useQuery({ queryKey: ['user-preferences'], queryFn: (): Promise<UserPreferences> => prefsApi.get().then(r => r.data), staleTime: 300_000 })
  const sys: MeasurementSystem = prefsData?.measurement_system ?? 'imperial'
  const unit = weightUnit(sys)

  const { data: events } = useQuery({ queryKey: ['spoilage'], queryFn: () => spoilageApi.list().then(r => r.data.events) })
  const { data: report } = useQuery({ queryKey: ['spoilage-report'], queryFn: () => spoilageApi.report().then(r => r.data) })
  const del = useMutation({
    mutationFn: (id: string) => spoilageApi.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['spoilage'] }); qc.invalidateQueries({ queryKey: ['spoilage-report'] }) },
  })

  const fmtKg = (kg: number) => `${kgToDisplayUnit(kg, sys).toFixed(1)} ${unit}`
  const usd = (n?: number) => n != null ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'

  return (
    <main id="main-content" style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Trash2 size={20} /> Spoilage & loss
        </h1>
        <button onClick={() => setShowModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600 }}>
          <Plus size={15} /> Log spoilage
        </button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 18 }}>
        Track fruit you lost so you can see where it's going — and what it cost.
      </p>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 18 }}>
        <div style={card}><div style={lbl}>Total lost</div><div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)' }}>{fmtKg(report?.total_kg ?? 0)}</div></div>
        <div style={card}><div style={lbl}>Lost value</div><div style={{ fontSize: 22, fontWeight: 700, color: '#dc2626' }}>{usd(report?.total_value)}</div></div>
        <div style={card}><div style={lbl}>Events</div><div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)' }}>{report?.count ?? 0}</div></div>
      </div>

      {/* By type breakdown */}
      {!!report?.by_type?.length && (
        <div style={{ ...card, marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 10 }}>By loss type</div>
          {report.by_type.map(b => {
            const m = typeMeta(b.key)
            const pct = report.total_kg ? (b.quantity_kg / report.total_kg) * 100 : 0
            return (
              <div key={b.key} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--color-text)', marginBottom: 3 }}>
                  <span style={{ fontWeight: 600 }}>{m.label}</span>
                  <span style={{ color: 'var(--color-text-muted)' }}>{fmtKg(b.quantity_kg)} · {usd(b.value_usd)} · {b.count}×</span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: 'var(--color-bg)', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: m.color }} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* By block breakdown — where loss concentrates */}
      {!!report?.by_block?.length && (
        <div style={{ ...card, marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 10 }}>By block</div>
          {report.by_block.map(b => {
            const pct = report.total_kg ? (b.quantity_kg / report.total_kg) * 100 : 0
            return (
              <div key={b.key || b.label} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--color-text)', marginBottom: 3 }}>
                  <span style={{ fontWeight: 600 }}>{b.label}</span>
                  <span style={{ color: 'var(--color-text-muted)' }}>{fmtKg(b.quantity_kg)} · {usd(b.value_usd)} · {b.count}×</span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: 'var(--color-bg)', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: '#d97706' }} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Recent events */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 10 }}>Recent loss events</div>
        {!events?.length ? (
          <p style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>No spoilage logged yet. Click "Log spoilage" to record fruit you lost.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {events.map(e => {
              const m = typeMeta(e.spoilage_type)
              return (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, background: 'var(--color-bg)' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--color-text)', fontWeight: 600 }}>
                      {m.label}{e.variety_name ? ` · ${e.variety_name}` : ''}{e.block_name ? ` · ${e.block_name}` : ''}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>
                      {e.occurred_at}{e.reason ? ` · ${e.reason}` : ''}{e.notes ? ` · ${e.notes}` : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--color-text)', textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 600 }}>{fmtKg(e.quantity_kg)}</div>
                    {e.value_usd != null && <div style={{ fontSize: 11, color: '#dc2626' }}>{usd(e.value_usd)}</div>}
                  </div>
                  <button onClick={() => del.mutate(e.id)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: 4 }}>
                    <X size={15} />
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {showModal && <LogModal onClose={() => setShowModal(false)} sys={sys} />}
    </main>
  )
}

function LogModal({ onClose, sys }: { onClose: () => void; sys: MeasurementSystem }) {
  const qc = useQueryClient()
  const unit = weightUnit(sys)
  const { data: varieties } = useQuery({ queryKey: ['varieties'], queryFn: () => varietiesApi.list().then(r => r.data.varieties) })
  const { data: blocks } = useQuery({ queryKey: ['blocks'], queryFn: () => blocksApi.list().then(r => r.data.blocks) })
  const [f, setF] = useState({ spoilage_type: 'not_picked' as SpoilageType, variety_id: '', block_id: '', quantity: '', value: '', reason: '', season: '', occurred_at: new Date().toISOString().slice(0, 10), notes: '' })
  const set = (k: keyof typeof f, v: string) => setF(p => ({ ...p, [k]: v }))
  const [valueTouched, setValueTouched] = useState(false)
  // Variety prices (§106) → auto-suggest the lost market value = kg × retail $/kg,
  // so growers don't have to compute it. Only until they type their own value.
  const { data: prices } = useQuery({ queryKey: ['pricing'], queryFn: () => pricingApi.list().then(r => r.data.prices) })
  useEffect(() => {
    if (valueTouched || !f.variety_id || !f.quantity) return
    const p = prices?.find(x => x.variety_id === f.variety_id)
    const cpk = p?.retail_cents_per_kg
    if (!cpk) return
    const v = inputToKg(parseFloat(f.quantity) || 0, sys) * cpk / 100
    if (v > 0) setF(prev => ({ ...prev, value: v.toFixed(0) }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.variety_id, f.quantity, prices, valueTouched])

  const save = useMutation({
    mutationFn: () => spoilageApi.create({
      spoilage_type: f.spoilage_type,
      variety_id: f.variety_id || undefined,
      block_id: f.block_id || undefined,
      quantity_kg: f.quantity ? inputToKg(parseFloat(f.quantity), sys) : 0,
      value_usd: f.value ? parseFloat(f.value) : undefined,
      reason: f.reason || undefined,
      season: f.season || undefined,
      occurred_at: f.occurred_at,
      notes: f.notes || undefined,
    } as Partial<SpoilageEvent>),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['spoilage'] }); qc.invalidateQueries({ queryKey: ['spoilage-report'] }); onClose()
    },
  })

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, width: 460, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)' }}>Log spoilage</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}><X size={18} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={lbl}>Loss type</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {TYPES.map(t => (
                <button key={t.v} type="button" onClick={() => set('spoilage_type', t.v)} title={t.hint}
                  style={{ flex: 1, padding: '7px 4px', fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${f.spoilage_type === t.v ? t.color : 'var(--color-border)'}`,
                    background: f.spoilage_type === t.v ? `color-mix(in srgb, ${t.color} 16%, transparent)` : 'var(--color-bg)',
                    color: f.spoilage_type === t.v ? t.color : 'var(--color-text-muted)' }}>
                  {t.label}
                </button>
              ))}
            </div>
            <p style={{ fontSize: 11, color: 'var(--color-text-faint)', marginTop: 4 }}>{typeMeta(f.spoilage_type).hint}</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={lbl}>Variety</label>
              <select style={inp} value={f.variety_id} onChange={e => set('variety_id', e.target.value)}>
                <option value="">— any —</option>
                {varieties?.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Block</label>
              <select style={inp} value={f.block_id} onChange={e => set('block_id', e.target.value)}>
                <option value="">— any —</option>
                {blocks?.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={lbl}>Quantity ({unit})</label><input style={inp} type="number" min={0} step={0.1} value={f.quantity} onChange={e => set('quantity', e.target.value)} placeholder="0" /></div>
            <div>
              <label style={lbl}>Lost value ($, optional){!valueTouched && f.value ? ' · auto' : ''}</label>
              <input style={inp} type="number" min={0} step={1} value={f.value} onChange={e => { setValueTouched(true); set('value', e.target.value) }} placeholder="estimate" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={lbl}>Reason</label>
              <select style={inp} value={f.reason} onChange={e => set('reason', e.target.value)}>
                <option value="">— none —</option>
                {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Date</label><input style={inp} type="date" value={f.occurred_at} onChange={e => set('occurred_at', e.target.value)} /></div>
          </div>
          <div><label style={lbl}>Notes</label><input style={inp} value={f.notes} onChange={e => set('notes', e.target.value)} placeholder="optional" /></div>
          {save.isError && <p style={{ fontSize: 12, color: '#dc2626' }}>Could not save — try again.</p>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-muted)' }}>Cancel</button>
            <button onClick={() => save.mutate()} disabled={save.isPending || !f.quantity} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: save.isPending || !f.quantity ? 'not-allowed' : 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, opacity: !f.quantity ? 0.5 : 1 }}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SpoilagePage
